import { useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let initialSessionChecked = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        // Only set loading false after we've also checked initial session
        if (initialSessionChecked) {
          setLoading(false);
        }

        // Check if user is banned
        if (event === 'SIGNED_IN' && session?.user) {
          const bannedUntil = session.user.banned_until;
          if (bannedUntil && new Date(bannedUntil) > new Date()) {
            await supabase.auth.signOut();
            setTimeout(() => {
              alert('Seu acesso foi desativado. Entre em contato com o administrador.');
            }, 100);
            return;
          }
        }

        if (event === 'SIGNED_IN' && window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      initialSessionChecked = true;
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signInWithMagicLink = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    return { error };
  }, []);

  const signUpWithPassword = useCallback(async (
    email: string,
    password: string,
    profile?: { displayName?: string; phone?: string }
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });

    if (!error && data.user) {
      // Update profile with optional fields
      if (profile?.displayName || profile?.phone) {
        await (supabase.from('profiles').update as any)({
          display_name: profile.displayName || null,
          phone: profile.phone || null,
        }).eq('user_id', data.user.id);
      }
    }

    return { data, error };
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  }, []);

  const updateProfile = useCallback(async (
    userId: string,
    profile: { displayName?: string; phone?: string }
  ) => {
    const { error } = await (supabase.from('profiles').update as any)({
      display_name: profile.displayName || null,
      phone: profile.phone || null,
    }).eq('user_id', userId);
    return { error };
  }, []);

  const getProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('display_name, phone, email')
      .eq('user_id', userId)
      .single() as { data: { display_name: string | null; phone: string | null; email: string | null } | null; error: any };
    return { data, error };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();

    // Clear all user-related storage to prevent data leaking between sessions
    try {
      // Clear specific productdocs keys from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('productdocs_') || key.startsWith('sb-'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));

      // Clear sessionStorage entirely
      sessionStorage.clear();
    } catch (e) {
      // Storage access may fail in some contexts; ignore
    }

    return { error };
  }, []);

  return {
    user,
    session,
    loading,
    signInWithMagicLink,
    signUpWithPassword,
    signInWithPassword,
    updateProfile,
    getProfile,
    resetPassword,
    updatePassword,
    signOut,
  };
}
